/**
 * Splits outreach wording into the pieces the Edit wording read view draws as
 * chips. The grammar is the sequence renderer's:
 *
 *   {{FieldName}}              a merge field
 *   {{#Flag}} … {{/Flag}}      shown when the flag is true
 *   {{^Flag}} … {{/Flag}}      shown when the flag is false
 *
 * This is a DISPLAY aid only. It never decides whether wording is valid — an
 * unbalanced block or an unknown field is the server's to refuse, on save and in
 * the preview's findings — so it tokenises whatever it is given and marks an
 * unknown name rather than rejecting it.
 */

export type TemplateToken =
  | { kind: "text"; text: string }
  | { kind: "field"; name: string; raw: string }
  | { kind: "open"; name: string; negated: boolean; raw: string }
  | { kind: "close"; name: string; raw: string };

const TAG = /\{\{\s*([#^/]?)\s*([A-Za-z][A-Za-z0-9]*)\s*\}\}/g;

export function tokenizeTemplate(text: string): TemplateToken[] {
  const out: TemplateToken[] = [];
  let last = 0;
  for (const m of text.matchAll(TAG)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: "text", text: text.slice(last, at) });
    const [raw, sigil, name] = m;
    if (sigil === "#" || sigil === "^") {
      out.push({ kind: "open", name, negated: sigil === "^", raw });
    } else if (sigil === "/") {
      out.push({ kind: "close", name, raw });
    } else {
      out.push({ kind: "field", name, raw });
    }
    last = at + raw.length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/**
 * The condition chips' words. Listing's pair is the one the owner named ("if
 * listing" / "if no listing"); any other flag reads from its name so a flag the
 * server adds later still renders as a condition rather than a blank.
 */
const CONDITION_WORDS: Record<string, { when: string; unless: string }> = {
  Listing: { when: "if listing", unless: "if no listing" },
  PrequalIntroSent: {
    when: "if prequal intro sent",
    unless: "if prequal intro not sent",
  },
};

export function conditionLabel(name: string, negated: boolean): string {
  const words = CONDITION_WORDS[name];
  if (words) return negated ? words.unless : words.when;
  return negated ? `if not ${name}` : `if ${name}`;
}

export const CONDITION_END_LABEL = "end";

/** The text inserted for a field. */
export function fieldTag(name: string): string {
  return `{{${name}}}`;
}

/**
 * Insert `snippet` over the current selection of `value`. Returns the new text
 * and where the caret belongs afterwards — just past what was inserted.
 */
export function insertAt(
  value: string,
  snippet: string,
  selectionStart: number | null | undefined,
  selectionEnd: number | null | undefined,
): { text: string; caret: number } {
  const start = clampIndex(selectionStart, value.length);
  const end = Math.max(start, clampIndex(selectionEnd, value.length));
  return {
    text: value.slice(0, start) + snippet + value.slice(end),
    caret: start + snippet.length,
  };
}

function clampIndex(i: number | null | undefined, length: number): number {
  if (i == null || Number.isNaN(i)) return length;
  return Math.min(Math.max(0, i), length);
}
